import React, { useState, useEffect } from 'react';
import { BarChart2, Trophy, Users, Gift, Crown, MessageCircle, Medal, ChevronDown, Info } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import { collection, query, orderBy, getDocs, limit, collectionGroup } from 'firebase/firestore';
import { db } from '../firebase/config';
import { ARCHIVED_BOUNTIES } from './BountyArchive';
import { format, startOfDay, parseISO } from 'date-fns';
import { RANK_COLORS, type UserRank } from '../types/user';

// Calculate total rewards and entries from archived bounties
const totalRewards = ARCHIVED_BOUNTIES.reduce((sum, bounty) => sum + bounty.reward, 0);

interface DailyRegistrations {
  date: string;
  count: number;
}

interface LeaderboardUser {
  username: string;
  rank: UserRank;
  totalPoints: number;
}

export default function BountyAnalytics() {
  const [registrationData, setRegistrationData] = useState<DailyRegistrations[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const [leaderboardUsers, setLeaderboardUsers] = useState<LeaderboardUser[]>([]);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get all submissions from all bounty subcollections
        const submissionsQuery = collectionGroup(db, 'submissions');
        const submissionsSnapshot = await getDocs(submissionsQuery);
        setTotalEntries(submissionsSnapshot.size);
        
        // Rest of the existing data fetching...
        const usersRef = collection(db, 'users');
        const q = query(usersRef, orderBy('createdAt', 'asc'));
        const querySnapshot = await getDocs(q);
        
        // Group users by registration date
        const registrationsByDate = new Map<string, number>();
        
        querySnapshot.forEach(doc => {
          const userData = doc.data();
          const createdAt = parseISO(userData.createdAt);
          const dateKey = format(startOfDay(createdAt), 'yyyy-MM-dd');
          
          registrationsByDate.set(
            dateKey,
            (registrationsByDate.get(dateKey) || 0) + 1
          );
        });
        
        // Convert to array and sort by date
        const sortedData = Array.from(registrationsByDate.entries())
          .map(([date, count]) => ({ date, count }))
          .sort((a, b) => a.date.localeCompare(b.date));
        
        setRegistrationData(sortedData);
        setTotalUsers(querySnapshot.size);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    const fetchLeaderboard = async () => {
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, orderBy('totalPoints', 'desc'));
        const querySnapshot = await getDocs(q);
        
        const users: LeaderboardUser[] = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            username: data.username,
            rank: data.rank,
            totalPoints: data.totalPoints
          };
        });
        
        setLeaderboardUsers(users);
      } catch (err) {
        console.error('Error fetching leaderboard:', err);
      }
    };

    fetchData();
    fetchLeaderboard();
  }, []);

  const userTrendData = {
    labels: registrationData.map(item => format(parseISO(item.date), 'MMM d')),
    datasets: [
      {
        label: 'Daily Registrations',
        data: registrationData.map(item => item.count),
        borderColor: 'rgba(239, 68, 68, 1)',
        backgroundColor: 'rgba(239, 68, 68, 0.2)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'rgba(255, 255, 255, 0.8)',
        bodyColor: 'rgba(255, 255, 255, 0.8)',
        callbacks: {
          title: (context: any) => {
            const index = context[0].dataIndex;
            if (index >= 0 && index < registrationData.length) {
              return format(parseISO(registrationData[index].date), 'PPP');
            }
            return '';
          },
          label: (context: any) => {
            return `New Users: ${context.raw.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          maxRotation: 45
        }
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          callback: (value: number) => value.toLocaleString()
        },
        beginAtZero: true
      }
    }
  };

  const displayedUsers = showAllUsers ? leaderboardUsers : leaderboardUsers.slice(0, 10);

  if (error) {
    return (
      <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-red-200">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-black/40 backdrop-blur-sm rounded-xl shadow-2xl p-4 md:p-8 border border-white/10">
        <div className="flex items-center space-x-3 mb-6">
          <BarChart2 className="w-8 h-8 text-red-600" />
          <h1 className="text-2xl md:text-3xl font-bold">Bounty Analytics</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          {/* Total Bounties */}
          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
            <div className="flex items-center space-x-2 mb-2">
              <Trophy className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-semibold">Total Bounties</h3>
            </div>
            <p className="text-3xl font-bold">{ARCHIVED_BOUNTIES.length}</p>
            <p className="text-sm text-gray-400 mt-1">In Archive</p>
          </div>

          {/* Total Entries */}
          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
            <div className="flex items-center space-x-2 mb-2">
              <MessageCircle className="w-5 h-5 text-green-500" />
              <h3 className="text-lg font-semibold">Total Entries</h3>
            </div>
            <p className="text-3xl font-bold">{totalEntries}</p>
            <p className="text-sm text-gray-400 mt-1">Submitted Solutions</p>
          </div>

          {/* Total Users */}
          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
            <div className="flex items-center space-x-2 mb-2">
              <Users className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-semibold">Total Users</h3>
            </div>
            <p className="text-3xl font-bold">{totalUsers}</p>
            <p className="text-sm text-gray-400 mt-1">Registered Users</p>
          </div>

          {/* Total Rewards */}
          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
            <div className="flex items-center space-x-2 mb-2">
              <Gift className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-semibold">Total Rewards</h3>
            </div>
            <p className="text-3xl font-bold">{totalRewards}</p>
            <p className="text-sm text-gray-400 mt-1">USDC Distributed</p>
          </div>

          {/* Grandmaster Pool */}
          <div className="relative bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
            <div className="flex items-center space-x-2 mb-2">
              <Crown className="w-5 h-5 text-yellow-500" />
              <h3 className="text-lg font-semibold">Grandmaster Pool</h3>
            </div>
            <p className="text-3xl font-bold">$5000</p>
            <div className="flex items-center space-x-2 mt-1">
              <p className="text-sm text-gray-400">USDC</p>
              <button
                className="group/tooltip relative"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <Info className="w-4 h-4 text-gray-400 hover:text-white transition-colors" />
                {showTooltip && (
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-black/90 backdrop-blur-sm text-xs text-gray-200 p-2 rounded-lg border border-white/10 shadow-xl z-50">
                    Grandmaster pool value will accrue over time. Fees are generated through a 2% sales tax on shogun - A portion of these fees will be allocated to the pool.
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">User Registration Trend</h3>
            {loading && (
              <div className="text-sm text-gray-400">Loading data...</div>
            )}
          </div>
          <div className="h-[300px]">
            {!loading && registrationData.length > 0 ? (
              <Line data={userTrendData} options={lineChartOptions} />
            ) : !loading && registrationData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400">
                No registration data available
              </div>
            ) : null}
          </div>
        </div>

        {/* Leaderboard Section */}
        <div className="mt-8 bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Medal className="w-5 h-5 text-yellow-500" />
              <h3 className="text-lg font-semibold">Leaderboard</h3>
            </div>
          </div>

          <div className="overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-white/10">
                  <th className="pb-2 text-sm font-medium text-gray-400">Rank</th>
                  <th className="pb-2 text-sm font-medium text-gray-400">Username</th>
                  <th className="pb-2 text-sm font-medium text-gray-400">Title</th>
                  <th className="pb-2 text-sm font-medium text-gray-400 text-right">Points</th>
                </tr>
              </thead>
              <tbody>
                {displayedUsers.map((user, index) => (
                  <tr key={user.username} className="border-b border-white/5 last:border-0">
                    <td className="py-3 text-sm">#{index + 1}</td>
                    <td className="py-3">
                      <span className="font-medium">{user.username}</span>
                    </td>
                    <td className="py-3">
                      <span className={`text-sm font-medium ${RANK_COLORS[user.rank].text}`}>
                        {user.rank}
                      </span>
                    </td>
                    <td className="py-3 text-right font-mono">{user.totalPoints.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {leaderboardUsers.length > 10 && (
              <button
                onClick={() => setShowAllUsers(!showAllUsers)}
                className="w-full mt-4 py-2 px-4 bg-black/20 hover:bg-black/30 rounded-lg text-sm text-gray-400 hover:text-white transition-colors flex items-center justify-center space-x-1"
              >
                <span>{showAllUsers ? 'Show Less' : 'Show All'}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showAllUsers ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}